import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const TEAM_IMG =
  "https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=1400";

const values = [
  { k: "I", t: "Clarity", d: "Ruthless prioritization. The right thing before the many things." },
  { k: "II", t: "Craft", d: "The details are the difference. We sweat every one of them." },
  { k: "III", t: "Candor", d: "Advisors, not agreers. We say what needs to be said — respectfully." },
  { k: "IV", t: "Compounding", d: "Small, correct decisions repeated. That's the whole game." },
];

export default function LuxeAbout() {
  return (
    <div data-testid="luxe-about">
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 md:pt-24 pb-20">
        <div className="font-fraunces italic text-[#b8964f] text-sm tracking-widest uppercase mb-6">
          About the firm
        </div>
        <h1 className="luxe-hero font-serif-display text-[#0a1f5c] font-medium max-w-5xl">
          A boutique firm.
          <br />
          <em className="italic text-[#b8964f]">An enterprise mindset.</em>
        </h1>
        <p className="mt-10 max-w-3xl text-lg md:text-xl font-fraunces text-[#3a3a37] leading-relaxed">
          Based in Indianapolis and operating nationwide, MKA Business Solutions
          partners with founders, family businesses, and mid-market leaders to
          unlock the next chapter of growth — with the strategic rigor of a
          global consultancy and the accountability of a trusted advisor.
        </p>
      </section>

      <section className="border-y border-[#e6dbc7] bg-[#f0e8d8]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
          <div className="md:col-span-6 order-2 md:order-1">
            <div className="font-fraunces italic text-[#b8964f] text-sm tracking-widest uppercase mb-6">
              Our mission
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-[#0a1f5c]">
              To help ambitious organizations move with{" "}
              <em className="italic text-[#b8964f]">clarity</em>, courage, and speed.
            </h2>
            <p className="mt-6 font-fraunces text-[#3a3a37] text-lg leading-relaxed">
              We believe the best strategy is one that gets executed. So every
              engagement — from a positioning refresh to a full operational
              overhaul — is designed to leave your team stronger than we found
              them.
            </p>
          </div>
          <div className="md:col-span-6 order-1 md:order-2 relative">
            <div className="gold-frame">
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src={TEAM_IMG}
                  alt="MKA team"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="absolute -bottom-6 -right-4 md:-bottom-8 md:-right-8 bg-[#0a1f5c] text-[#d4b478] px-6 py-4">
              <div className="font-fraunces italic text-xs tracking-widest uppercase">
                Est. 2024
              </div>
              <div className="font-serif-display text-2xl mt-1">
                Since day one.
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-24 md:py-32">
        <div className="font-fraunces italic text-[#b8964f] text-sm tracking-widest uppercase mb-6">
          What we value
        </div>
        <h2 className="font-serif-display text-5xl md:text-6xl lg:text-7xl text-[#0a1f5c] max-w-3xl leading-[0.95]">
          Four principles.
          <br />
          <em className="italic text-[#b8964f]">Every engagement.</em>
        </h2>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16">
          {values.map((v, i) => (
            <div
              key={v.t}
              data-testid={`luxe-value-${i}`}
              className="border-t border-[#e6dbc7] pt-8"
            >
              <div className="flex items-baseline gap-4">
                <span className="font-serif-editorial italic text-[#b8964f] text-2xl">
                  {v.k}
                </span>
                <h3 className="font-serif-display text-4xl md:text-5xl text-[#0a1f5c]">
                  {v.t}
                </h3>
              </div>
              <p className="mt-4 font-fraunces text-[#3a3a37] text-lg leading-relaxed max-w-md">
                {v.d}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* MEET THE FOUNDER - full profile */}
      <section className="border-y border-[#e6dbc7] bg-[#fbf8f3]" data-testid="founder-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-start">
          <div className="md:col-span-5">
            <div className="gold-frame">
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_mka-company/artifacts/bbpjn5xl_Profile%20Pic.jpg"
                  alt="Margo Kerr-Armour, Founder of MKA Business Solutions"
                  className="w-full h-full object-cover"
                  data-testid="founder-photo"
                />
              </div>
            </div>
            <div className="mt-10 md:mt-14">
              <div className="font-fraunces italic text-[#b8964f] text-sm tracking-widest uppercase mb-3">
                Meet the founder
              </div>
              <h2 className="font-serif-display text-5xl md:text-6xl text-[#0a1f5c] leading-[0.98]">
                Margo
                <br />
                <em className="italic text-[#b8964f]">Kerr-Armour</em>
              </h2>
              <div className="mt-4 font-fraunces text-[#3a3a37] text-lg italic">
                Founder & Principal · MKA Business Solutions
              </div>
            </div>
          </div>

          <div className="md:col-span-7">
            <p className="font-serif-editorial text-2xl md:text-[28px] text-[#0a1f5c] leading-[1.4] font-light">
              Founded by <em className="italic text-[#b8964f]">Margo Kerr-Armour</em>,
              MKA Business Solutions draws on more than three decades of
              executive experience in finance, operations, and business
              strategy. Across her career, she has helped organizations improve
              profitability, streamline operations, and build the scalable
              systems that support sustainable growth.
            </p>
            <div className="mt-8 space-y-5 font-fraunces text-[#3a3a37] text-lg leading-relaxed">
              <p>
                Her background spans executive leadership, process improvement,
                financial management, and organizational transformation. Today,
                she brings that same discipline to business owners and
                leadership teams who are ready to move beyond ideas and into
                measurable results.
              </p>
              <p>
                Whether it's implementing AI into daily operations, refining a
                brand's positioning, or providing hands-on executive
                consulting, every engagement starts with the same question:
              </p>
              <p className="font-serif-editorial italic text-2xl md:text-3xl text-[#b8964f] leading-snug py-4 border-l-2 border-[#b8964f] pl-6">
                What's preventing this business from reaching its next level?
              </p>
              <p>
                We don't believe in one-size-fits-all solutions. Every business
                has its own challenges, ambitions, and blind spots — our role
                is to build the strategy and the operating foundation that fit
                yours.
              </p>
              <p className="text-[#0a1f5c] font-medium">
                If you're ready to scale with intention and build something
                designed to last, request a consultation and let's talk about
                what's next.
              </p>
            </div>

            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-8">
              <div className="border-t border-[#b8964f] pt-5">
                <div className="font-fraunces italic text-[#b8964f] text-xs tracking-widest uppercase mb-2">
                  Credential
                </div>
                <div className="font-serif-display text-xl md:text-2xl text-[#0a1f5c] leading-snug">
                  M.A. in Management <em className="italic text-[#b8964f]">&</em> Leadership
                </div>
              </div>
              <div className="border-t border-[#b8964f] pt-5">
                <div className="font-fraunces italic text-[#b8964f] text-xs tracking-widest uppercase mb-2">
                  Credential
                </div>
                <div className="font-serif-display text-xl md:text-2xl text-[#0a1f5c] leading-snug">
                  B.S. in Business Administration · Minor, Professional Communication
                </div>
              </div>
              <div className="border-t border-[#b8964f] pt-5">
                <div className="font-fraunces italic text-[#b8964f] text-xs tracking-widest uppercase mb-2">
                  Experience
                </div>
                <div className="font-serif-display text-xl md:text-2xl text-[#0a1f5c] leading-snug">
                  30+ years — finance, operations <em className="italic text-[#b8964f]">&</em> strategy
                </div>
              </div>
              <div className="border-t border-[#b8964f] pt-5">
                <div className="font-fraunces italic text-[#b8964f] text-xs tracking-widest uppercase mb-2">
                  Practice
                </div>
                <div className="font-serif-display text-xl md:text-2xl text-[#0a1f5c] leading-snug">
                  Founder-led · Indianapolis-based
                </div>
              </div>
            </div>

            <Link
              to="/contact"
              data-testid="founder-cta"
              className="mt-12 inline-flex items-center gap-3 bg-[#0a1f5c] text-[#f7f2ea] px-8 py-4 rounded-full font-fraunces text-base hover:bg-[#b8964f] hover:text-[#0a1f5c] transition-all"
            >
              Request a Consultation <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-[#0a1f5c] text-[#f7f2ea]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-28 text-center">
          <h2 className="font-serif-display text-5xl md:text-6xl lg:text-7xl leading-tight max-w-4xl mx-auto">
            Let's put these principles to{" "}
            <em className="italic text-[#d4b478]">work</em> for you.
          </h2>
          <Link
            to="/contact"
            data-testid="luxe-about-cta"
            className="mt-10 inline-flex items-center gap-3 bg-[#d4b478] text-[#0a1f5c] px-10 py-5 rounded-full font-fraunces text-base hover:bg-[#f7f2ea] transition-all"
          >
            Begin a Conversation <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
